import type {
  FlightPlayerScoresFragment,
  HoleScoreFragment,
} from '../../../__generated__/hooks'
import {
  parForHoles,
  parForScoredHoles,
  sumForHoles,
  sumStablefordForHoles,
} from '../helpers'

/**
 * Subtotal / total row at the foot of front-9, back-9, and grand total.
 * `emphasized` styles the Tot row distinctly (green background + larger
 * number) so it reads as the round summary.
 *
 * The "Par N" label is the full par of the segment (a fixed reference), but
 * each player's to-par diff is computed against the par of the holes THEY have
 * actually scored — otherwise an unfinished round reads as a huge negative.
 *
 * Under `showStableford` the sub-line becomes the segment's stableford total
 * (e.g. `30 p`) — matching the per-hole `N p` stamps — instead of the
 * par-relative diff, since points are what the round is ranked on.
 */
/** Par-relative diff label for the sub-line: `E` / `+3` / `-2`. */
const fmtDiff = (diff: number): string => {
  if (diff === 0) return 'E'
  return diff > 0 ? `+${diff}` : String(diff)
}

const TotalRow = ({
  label,
  flight,
  holes,
  emphasized,
  showStableford,
}: {
  label: string
  flight: FlightPlayerScoresFragment[]
  holes: HoleScoreFragment[]
  emphasized?: boolean
  showStableford?: boolean
}) => {
  const holeNumbers = new Set(holes.map((h) => h.hole.number))
  const par = parForHoles(holes)
  return (
    <tr
      className="border-t border-ink-200"
      style={{
        background: emphasized ? 'var(--green-50)' : 'var(--cream-card)',
      }}
    >
      <th
        scope="row"
        className="text-left px-2 py-2 align-middle"
        style={{
          background: emphasized ? 'var(--green-50)' : 'var(--cream-card)',
        }}
      >
        <div
          className={`font-display text-[11px] font-extrabold ${
            emphasized ? 'text-green-900' : 'text-ink-700'
          }`}
          style={{
            letterSpacing: 'var(--ls-caps)',
            textTransform: 'uppercase',
          }}
        >
          {label}
        </div>
        <div className="font-mono num text-[10px] text-ink-500 leading-tight mt-0.5">
          Par {par}
        </div>
      </th>
      {flight.map((entry) => {
        const total = sumForHoles(entry, holeNumbers)
        const diff = total - parForScoredHoles(entry, holes)
        const points = sumStablefordForHoles(entry, holeNumbers)
        return (
          <td
            key={entry.id}
            className="text-center px-1 py-2 font-mono num leading-tight"
          >
            <div
              className={`font-bold ${
                emphasized ? 'text-green-900 text-base' : 'text-ink-900 text-sm'
              }`}
            >
              {total || '—'}
            </div>
            {total > 0 ? (
              <div className="text-[10px] text-ink-500 mt-0.5">
                {showStableford ? `${points} p` : fmtDiff(diff)}
              </div>
            ) : null}
          </td>
        )
      })}
    </tr>
  )
}

export default TotalRow
